import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { fmt } from '../helpers/helpers';
import { getAuthHeader, handleUnauthorized } from '../helpers/api';
import './CardsModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const WARNING_LABELS = {
  anchor_stale: 'âncora antiga',
  bank_low: 'limite baixo',
  bank_negative: 'âncora desatualizada',
  ceiling_exceeded: 'teto estourado',
  ceiling_exceeds_bank: 'teto acima do banco',
  debits_incomplete: 'compras sem valor',
  credit_source_conflict: 'dois sinais de pagamento'
};

const CREDIT_SOURCE_LABELS = {
  '': 'Automático (por valor e data)',
  notification: 'Notificação do banco',
  notebook: 'Linha do caderno'
};

const ANCHOR_SOURCE_LABELS = {
  'bank-app': 'App do banco',
  decline: 'Compra recusada',
  'invoice-payment': 'Pagamento de fatura',
  manual: 'Manual'
};

const emptyAnchorForm = () => ({
  available: '',
  at: moment().format('YYYY-MM-DDTHH:mm'),
  source: 'bank-app',
  note: '',
  limitTotal: ''
});

const CardsModal = ({ isOpen, onClose, userId }) => {
  const [cards, setCards] = useState([]);
  const [knownCardAccounts, setKnownCardAccounts] = useState([]);
  const [expenseMetadata, setExpenseMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [anchorForm, setAnchorForm] = useState(emptyAnchorForm());
  const [ceilingForm, setCeilingForm] = useState({ enabled: false, amount: '' });
  const [snapshotForm, setSnapshotForm] = useState({ amount: '', at: '', note: '' });
  const [linkMetadataId, setLinkMetadataId] = useState('');
  const [newCardAccount, setNewCardAccount] = useState('');

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: getAuthHeader(userId)
  }), [userId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [limitsRes, metadataRes] = await Promise.all([
        fetch(`${API_URL}/api/card-limits`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/financial-entries-metadata?kind=expense`, { headers: authHeaders() })
      ]);

      handleUnauthorized(limitsRes);
      if (!limitsRes.ok) throw new Error(`Failed to load card limits: ${limitsRes.status}`);

      const data = await limitsRes.json();
      setCards(data.cards || []);
      setKnownCardAccounts(data.knownCardAccounts || []);
      setExpenseMetadata(metadataRes.ok ? (await metadataRes.json()).metadata || [] : []);
    } catch (err) {
      console.error('Failed to load card limits:', err);
      setError('Erro ao carregar cartões. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const post = async (path, body) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/card-limits/${path}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      handleUnauthorized(res);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Erro ao salvar. Tente novamente.');
        return null;
      }
      await load();
      return data.card;
    } catch (err) {
      console.error('Card limit write failed:', err);
      setError('Erro ao salvar. Tente novamente.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (cardAccount) => {
    const card = cards.find((c) => c.cardAccount === cardAccount);
    setEditing(cardAccount);
    setLinkMetadataId('');
    setAnchorForm({
      ...emptyAnchorForm(),
      limitTotal: card?.bank?.limitTotal != null ? String(card.bank.limitTotal) : ''
    });
    setCeilingForm({
      enabled: card?.ceiling?.enabled === true,
      amount: card?.ceiling?.amount != null ? String(card.ceiling.amount) : '',
    });
    setSnapshotForm({ amount: '', at: moment().format('YYYY-MM-DDTHH:mm'), note: '' });
  };

  const handleAnchorSave = async () => {
    const available = Number(anchorForm.available);
    if (anchorForm.available === '' || !Number.isFinite(available)) {
      setError('Informe o limite disponível que o banco mostra.');
      return;
    }
    const saved = await post('anchor', {
      cardAccount: editing,
      available,
      at: moment(anchorForm.at).toISOString(),
      source: anchorForm.source,
      note: anchorForm.note || undefined,
      limitTotal: anchorForm.limitTotal === '' ? null : Number(anchorForm.limitTotal)
    });
    if (saved) setAnchorForm({ ...emptyAnchorForm(), limitTotal: anchorForm.limitTotal });
  };

  const handleCeilingSave = () => post('ceiling', {
    cardAccount: editing,
    enabled: ceilingForm.enabled,
    amount: ceilingForm.amount === '' ? null : Number(ceilingForm.amount)
  });

  const handleSnapshotSave = async () => {
    if (snapshotForm.amount === '' || !Number.isFinite(Number(snapshotForm.amount))) {
      setError('Informe o total da fatura que o app do cartão mostra.');
      return;
    }
    const saved = await post('invoice-snapshot', {
      cardAccount: editing,
      amount: Number(snapshotForm.amount),
      at: moment(snapshotForm.at).toISOString(),
      note: snapshotForm.note || undefined
    });
    if (saved) setSnapshotForm({ amount: '', at: moment().format('YYYY-MM-DDTHH:mm'), note: '' });
  };

  const handleCreditSource = (creditSource) => post('credit-source', { cardAccount: editing, creditSource });

  const handleLink = async (metadataId, unlink) => {
    await post('note-links', { cardAccount: editing, metadataId, unlink });
    setLinkMetadataId('');
  };

  if (!isOpen) return null;

  const editingCard = cards.find((c) => c.cardAccount === editing) || null;
  const trackedAccounts = new Set(cards.map((c) => c.cardAccount));
  const availableToAdd = knownCardAccounts.filter((c) => !trackedAccounts.has(c.cardAccount));

  return (
    <div className="cards-modal-backdrop" onClick={onClose}>
      <div className="cards-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cards-modal-header">
          <h2>
            {editing ? (
              <>
                <button className="cards-back-btn" onClick={() => setEditing(null)} title="Voltar">
                  <i className="fa-solid fa-arrow-left"></i>
                </button>
                {editing}
              </>
            ) : 'Cartões'}
          </h2>
          <button className="cards-close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="cards-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="cards-modal-content">
          {loading ? (
            <div className="cards-loading">Carregando...</div>
          ) : editing ? (
            <CardEditor
              cardAccount={editing}
              card={editingCard}
              anchorForm={anchorForm}
              setAnchorForm={setAnchorForm}
              ceilingForm={ceilingForm}
              setCeilingForm={setCeilingForm}
              snapshotForm={snapshotForm}
              setSnapshotForm={setSnapshotForm}
              expenseMetadata={expenseMetadata}
              linkMetadataId={linkMetadataId}
              setLinkMetadataId={setLinkMetadataId}
              onAnchorSave={handleAnchorSave}
              onCeilingSave={handleCeilingSave}
              onSnapshotSave={handleSnapshotSave}
              onLink={handleLink}
              onCreditSource={handleCreditSource}
              saving={saving}
            />
          ) : (
            <>
              {cards.length === 0 ? (
                <div className="cards-empty">
                  Nenhum cartão acompanhado ainda. Escolha um cartão abaixo e registre
                  o limite disponível que o app do banco mostra.
                </div>
              ) : (
                <div className="cards-list">
                  {cards.map((card) => (
                    <CardRow key={card.cardAccount} card={card} onEdit={() => openEditor(card.cardAccount)} />
                  ))}
                </div>
              )}

              {availableToAdd.length > 0 && (
                <div className="cards-add">
                  <select value={newCardAccount} onChange={(e) => setNewCardAccount(e.target.value)}>
                    <option value="">Adicionar cartão...</option>
                    {availableToAdd.map((card) => (
                      <option key={card.cardAccount} value={card.cardAccount}>
                        {card.cardAccount}
                        {card.cardLast4.length > 0 ? ` (final ${card.cardLast4.join(', ')})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    disabled={!newCardAccount}
                    onClick={() => { openEditor(newCardAccount); setNewCardAccount(''); }}
                  >
                    Acompanhar
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="cards-modal-footer">
          <span className="cards-floor-hint">
            O valor mostrado é sempre menor ou igual ao limite real — nunca sobe sozinho.
          </span>
          <button className="cards-done-btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

const CardRow = ({ card, onEdit }) => {
  const { bank, ceiling } = card;
  const ceilingPct = ceiling.amount && ceiling.remaining != null
    ? Math.max(0, Math.min(100, (ceiling.remaining / ceiling.amount) * 100))
    : null;

  return (
    <div className="card-row">
      <div className="card-row-main">
        <div className="card-row-identity">
          <span className="card-row-name">{card.cardAccount}</span>
          {card.cardLast4.length > 0 && (
            <span className="card-row-last4">final {card.cardLast4.join(' · ')}</span>
          )}
        </div>
        <button className="card-row-edit" onClick={onEdit} title="Editar">
          <i className="fa-solid fa-pen"></i>
        </button>
      </div>

      <div className="card-row-figures">
        <div className="card-figure">
          <span className="card-figure-label">Banco</span>
          <span className={`card-figure-value ${bank.available != null && bank.available < 0 ? 'negative' : ''}`}>
            {bank.available != null ? fmt(bank.available) : '—'}
            {bank.limitTotal != null && <small> de {fmt(bank.limitTotal)}</small>}
          </span>
          {bank.anchorAgeDays != null && (
            <span className={`card-badge ${bank.anchorAgeDays > 30 ? 'warn' : ''}`}>
              âncora {bank.anchorAgeDays}d
            </span>
          )}
        </div>

        <div className="card-figure">
          <span className="card-figure-label">Teto</span>
          <span className={`card-figure-value ${ceiling.remaining != null && ceiling.remaining < 0 ? 'negative' : ''}`}>
            {ceiling.enabled && ceiling.remaining != null ? fmt(ceiling.remaining) : '—'}
            {ceiling.enabled && ceiling.amount != null && <small> de {fmt(ceiling.amount)}</small>}
          </span>
          {ceilingPct != null && ceiling.enabled && (
            <span className="card-progress">
              <span className="card-progress-fill" style={{ width: `${ceilingPct}%` }} />
            </span>
          )}
        </div>

        <div className="card-figure">
          <span className="card-figure-label">Ciclo fecha</span>
          <span className="card-figure-value">
            {card.cycle?.closesAt ? moment(card.cycle.closesAt).format('D/MMM') : '—'}
          </span>
        </div>
      </div>

      {card.warnings.length > 0 && (
        <div className="card-row-warnings">
          {card.warnings.map((warning) => (
            <span key={warning} className="card-warning-chip">
              {WARNING_LABELS[warning] || warning}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const CardEditor = ({
  cardAccount, card, anchorForm, setAnchorForm, ceilingForm, setCeilingForm,
  snapshotForm, setSnapshotForm, expenseMetadata, linkMetadataId, setLinkMetadataId,
  onAnchorSave, onCeilingSave, onSnapshotSave, onLink, onCreditSource, saving
}) => {
  const linked = card?.noteLinks || [];
  const linkedIds = new Set(linked.map((link) => link.metadataId));
  // An archived label is no longer in the metadata list, but the link stores
  // the name it had — better than showing a raw id.
  const linkName = (link) => expenseMetadata.find((m) => m.id === link.metadataId)?.name
    || link.labelNormalized
    || link.metadataId;

  return (
    <div className="card-editor">
      {card && (
        <div className="card-editor-readout">
          {card.display}
        </div>
      )}

      <section className="card-editor-section">
        <h3>Re-ancorar limite do banco</h3>
        <p className="card-editor-hint">
          Registre o disponível que o app do banco mostra agora. É a única coisa que
          faz o número subir.
        </p>
        <div className="card-editor-grid">
          <label>
            Disponível
            <input
              type="number"
              step="0.01"
              min="0"
              value={anchorForm.available}
              onChange={(e) => setAnchorForm({ ...anchorForm, available: e.target.value })}
              placeholder="0,00"
            />
          </label>
          <label>
            Quando
            <input
              type="datetime-local"
              value={anchorForm.at}
              onChange={(e) => setAnchorForm({ ...anchorForm, at: e.target.value })}
            />
          </label>
          <label>
            Origem
            <select
              value={anchorForm.source}
              onChange={(e) => setAnchorForm({ ...anchorForm, source: e.target.value })}
            >
              {Object.entries(ANCHOR_SOURCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            Limite total
            <input
              type="number"
              step="0.01"
              min="0"
              value={anchorForm.limitTotal}
              onChange={(e) => setAnchorForm({ ...anchorForm, limitTotal: e.target.value })}
              placeholder="opcional"
            />
          </label>
          <label className="card-editor-wide">
            Observação
            <input
              type="text"
              value={anchorForm.note}
              onChange={(e) => setAnchorForm({ ...anchorForm, note: e.target.value })}
              placeholder="ex: print do app 08/09 15:39"
            />
          </label>
        </div>
        <button className="card-editor-save" onClick={onAnchorSave} disabled={saving}>
          Salvar âncora
        </button>
      </section>

      <section className="card-editor-section">
        <h3>Teto de gastos</h3>
        <p className="card-editor-hint">
          Quanto a fatura pode chegar. Comparado com a fatura de agora, não com o
          limite do banco.
        </p>
        <div className="card-editor-grid">
          <label className="card-editor-toggle">
            <input
              type="checkbox"
              checked={ceilingForm.enabled}
              onChange={(e) => setCeilingForm({ ...ceilingForm, enabled: e.target.checked })}
            />
            Ativo
          </label>
          <label>
            Valor
            <input
              type="number"
              step="0.01"
              min="0"
              value={ceilingForm.amount}
              onChange={(e) => setCeilingForm({ ...ceilingForm, amount: e.target.value })}
              placeholder="0,00"
            />
          </label>
        </div>
        <button className="card-editor-save" onClick={onCeilingSave} disabled={saving}>
          Salvar teto
        </button>
      </section>

      <section className="card-editor-section">
        <h3>Fatura de agora</h3>
        <p className="card-editor-hint">
          O total que o app do cartão mostra neste momento. É daqui que o teto conta:
          tudo capturado depois é somado. Sem isso o teto não tem de onde partir.
        </p>
        {card?.ceiling?.invoiceSnapshot ? (
          <div className="card-snapshot-current">
            Último registro: {fmt(card.ceiling.invoiceSnapshot.amount)} em{' '}
            {moment(card.ceiling.invoiceSnapshot.at).format('D/M/YY HH:mm')}
            {card.ceiling.invoiceNow != null && (
              <> · com as compras capturadas depois: <strong>{fmt(card.ceiling.invoiceNow)}</strong></>
            )}
          </div>
        ) : (
          <div className="card-snapshot-current empty">Nenhum registro ainda — o teto está sem base.</div>
        )}
        <div className="card-editor-grid">
          <label>
            Fatura agora
            <input
              type="number"
              step="0.01"
              min="0"
              value={snapshotForm.amount}
              onChange={(e) => setSnapshotForm({ ...snapshotForm, amount: e.target.value })}
              placeholder="0,00"
            />
          </label>
          <label>
            Quando
            <input
              type="datetime-local"
              value={snapshotForm.at}
              onChange={(e) => setSnapshotForm({ ...snapshotForm, at: e.target.value })}
            />
          </label>
          <label className="card-editor-wide">
            Observação
            <input
              type="text"
              value={snapshotForm.note}
              onChange={(e) => setSnapshotForm({ ...snapshotForm, note: e.target.value })}
              placeholder="ex: app Nu, fatura aberta"
            />
          </label>
        </div>
        <button className="card-editor-save" onClick={onSnapshotSave} disabled={saving}>
          Registrar fatura
        </button>
      </section>

      <section className="card-editor-section">
        <h3>Pagamento da fatura</h3>
        <p className="card-editor-hint">
          Um cartão, um caminho de crédito. Se o mesmo pagamento chegar pelos dois,
          o limite subiria em dobro — por isso só um conta.
        </p>
        <div className="card-editor-inline">
          <select value={card?.creditSource || ''} onChange={(e) => onCreditSource(e.target.value || null)} disabled={saving}>
            {Object.entries(CREDIT_SOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        {card?.ignoredCredits?.count > 0 && (
          <p className="card-editor-conflict">
            O outro sinal registrou {card.ignoredCredits.count} pagamento(s)
            ({fmt(card.ignoredCredits.total)}) que não estão sendo contados.
          </p>
        )}
      </section>

      <section className="card-editor-section">
        <h3>Linha da fatura no caderno</h3>
        <p className="card-editor-hint">
          Marcar essa linha como paga credita o limite do cartão. É o único sinal de
          pagamento que o Bradesco tem.
        </p>
        {linked.length > 0 && (
          <ul className="card-links">
            {linked.map((link) => (
              <li key={link.metadataId}>
                {linkName(link)}
                <button onClick={() => onLink(link.metadataId, true)} disabled={saving} title="Remover">
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="card-editor-inline">
          <select value={linkMetadataId} onChange={(e) => setLinkMetadataId(e.target.value)}>
            <option value="">Vincular categoria...</option>
            {expenseMetadata.filter((m) => !linkedIds.has(m.id)).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <button disabled={!linkMetadataId || saving} onClick={() => onLink(linkMetadataId, false)}>
            Vincular
          </button>
        </div>
      </section>

      {card?.history?.length > 0 && (
        <section className="card-editor-section">
          <h3>Histórico</h3>
          <ul className="card-history">
            {[...card.history].reverse().map((item, index) => (
              <li key={index}>
                <span className="card-history-when">{moment(item.at).format('D/M/YY HH:mm')}</span>
                <span className="card-history-field">{item.field}</span>
                <span className="card-history-change">
                  {formatHistoryValue(item.oldValue)} → {formatHistoryValue(item.newValue)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!card && (
        <div className="cards-empty">
          {cardAccount} ainda não tem âncora. Salve o limite disponível para começar a
          acompanhar.
        </div>
      )}
    </div>
  );
};

function formatHistoryValue(value) {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length === 0 ? '—' : `${value.length} vínculo(s)`;
  if (typeof value === 'number') return fmt(value);
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  const asDate = moment(value, moment.ISO_8601, true);
  return asDate.isValid() ? asDate.format('D/M/YY HH:mm') : String(value);
}

export default CardsModal;
